import { Link } from 'react-router-dom'
import { ArrowUpRight, Clock, Sparkles } from 'lucide-react'
import { translations } from '@/lib/translations'
import { SERVICES, getServiceCopy } from '@/lib/services'

export default function ServicesSection({ language }) {
  const t = translations[language]
  const lang = language === 'am' ? 'am' : 'en'

  return (
    <section id="services" className="relative py-4 px-4 md:py-28 lg:py-4 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 -left-40 w-80 h-80 bg-gradient-to-r from-primary/5 to-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-96 h-96 bg-gradient-to-l from-secondary/5 to-secondary/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-t from-primary/3 via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      <div
        className="absolute inset-0 opacity-[0.02] pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 1px)`,
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative max-w-7xl mx-auto z-10">
        <div className="text-center mb-16 md:mb-4 lg:mb-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 mb-6 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary/80 uppercase tracking-wide">
              Our Expertise
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent">
              {t.services.title}
            </span>
          </h2>

          <div className="relative flex justify-center">
            <div className="w-24 h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40 rounded-full" />
            <div className="absolute w-12 h-1 bg-primary rounded-full animate-pulse" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {SERVICES.map((service, index) => {
            const Icon = service.icon
            const copy = getServiceCopy(service, language)
            return (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className={`
                  group relative rounded-2xl
                  bg-card/80 backdrop-blur-sm
                  border border-border/50
                  transition-all duration-500 ease-out
                  hover:shadow-2xl hover:-translate-y-2
                  ${service.hoverBorder}
                  overflow-hidden
                `}
                style={{
                  animationDelay: `${index * 100}ms`,
                  animation: 'fadeInUp 0.6s ease-out forwards',
                  opacity: 0,
                }}
              >
                <div className="h-44 overflow-hidden bg-muted relative">
                  <img
                    src={service.images.hero}
                    alt={service.images.heroAlt[lang]}
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${service.images.heroPosition || 'object-center'}`}
                    referrerPolicy="no-referrer"
                    onError={(event) => {
                      event.currentTarget.src = '/placeholder.jpg'
                    }}
                  />
                  {copy.duration && (
                    <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/65 text-white text-xs font-medium">
                      <Clock className="w-3.5 h-3.5" />
                      {copy.duration}
                    </span>
                  )}
                </div>

                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br ${service.gradient} pointer-events-none`} />

                <div className="relative z-10 p-6 md:p-7">
                  <div className="relative mb-5">
                    <div className={`
                      w-14 h-14 rounded-xl
                      bg-gradient-to-br ${service.iconGradient}
                      flex items-center justify-center
                      shadow-lg group-hover:shadow-xl
                      transition-all duration-300
                      group-hover:scale-110 group-hover:rotate-3
                    `}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div className={`
                      absolute -inset-1 rounded-xl
                      bg-gradient-to-br ${service.iconGradient}
                      opacity-0 group-hover:opacity-30
                      blur-md transition-opacity duration-500
                    `} />
                  </div>

                  <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {copy.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {copy.shortDesc}
                  </p>
                  <span className="inline-flex items-center gap-2 text-primary text-sm font-medium">
                    {t.servicePage?.learnMore || 'View details'}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            )
          })}
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
  )
}
